import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, BadgeCheck, AlertTriangle, Newspaper, Scale, ShieldCheck } from "lucide-react";

const briefs = [
  {
    tag: "Economy",
    title: "RBI holds rates, markets cheer",
    body: "The central bank kept the repo rate unchanged at 6.5% for the eighth straight policy, citing easing inflation. The Sensex jumped 412 points while bank stocks led the rally. Analysts now expect the first rate cut early next quarter if monsoon rainfall stays on track.",
    words: 47,
    read: "18 sec",
    sources: 14,
  },
  {
    tag: "Science",
    title: "ISRO's reusable rocket clears key test",
    body: "ISRO successfully completed the third landing experiment of its reusable launch vehicle at Challakere. The winged body autonomously navigated to the runway after release from a helicopter at 4.5 km. The milestone cuts future satellite launch costs by an estimated 65%.",
    words: 45,
    read: "17 sec",
    sources: 11,
  },
  {
    tag: "Climate",
    title: "Monsoon arrives early, farmers relieved",
    body: "The southwest monsoon hit the Kerala coast four days ahead of schedule, the IMD confirmed. Early rains are expected to boost kharif sowing across peninsular India. Reservoir levels stand at 118% of the ten-year average, easing summer water stress in six states.",
    words: 45,
    read: "17 sec",
    sources: 9,
  },
];

const radar = [
  {
    name: "The Progressive Post",
    lean: "Left-leaning",
    pos: "15%",
    headline: "Transport Bill finally puts commuters before cars, activists say",
    tone: "Optimistic",
    color: "text-sky-400",
  },
  {
    name: "Centre Wire",
    lean: "Center",
    pos: "50%",
    headline: "Urban Transport Bill passes with mixed reactions from states",
    tone: "Neutral",
    color: "text-cyan-400",
  },
  {
    name: "Heritage Daily",
    lean: "Right-leaning",
    pos: "85%",
    headline: "New Transport Bill raises concerns over taxpayer burden",
    tone: "Skeptical",
    color: "text-amber-400",
  },
];

const signals = [
  { label: "Source authenticity", value: 45 },
  { label: "Cross-source match", value: 28 },
  { label: "Media forensics", value: 22 },
  { label: "Expert consensus", value: 35 },
];

const tabs = [
  { id: "briefs", label: "60-Word Briefs", icon: Newspaper },
  { id: "radar", label: "Source Radar", icon: Scale },
  { id: "fact", label: "Fact Inspector", icon: ShieldCheck },
];

const Playground = () => {
  const [tab, setTab] = useState("briefs");
  const [briefIdx, setBriefIdx] = useState(0);

  const brief = briefs[briefIdx];

  return (
    <section id="playground" data-testid="playground-section" className="py-20 lg:py-32 border-t border-[#1E293B] bg-[#0B111C]/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="mono-label">Try It Now</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Don't take our word for it. Play with it.
          </h2>
          <p className="text-slate-400 mt-4 leading-relaxed">
            A live slice of the InNewZ experience — real interaction, no sign-up needed.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`playground-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                tab === t.id
                  ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                  : "border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-[#1E293B] bg-gradient-to-b from-slate-800/50 to-slate-900/80 p-6 sm:p-10 min-h-[380px]">
          <AnimatePresence mode="wait">
            {tab === "briefs" && (
              <motion.div
                key={`brief-${briefIdx}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
                className="max-w-2xl"
                data-testid="brief-reader-panel"
              >
                <div className="flex items-center gap-3">
                  <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-mono-x uppercase tracking-widest text-amber-300">
                    {brief.tag}
                  </span>
                  <span className="text-xs font-mono-x text-slate-500">
                    {briefIdx + 1} / {briefs.length}
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold mt-4">{brief.title}</h3>
                <p className="text-slate-300 leading-relaxed mt-3">{brief.body}</p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-xs font-mono-x uppercase tracking-widest text-slate-500">
                  <span>{brief.words} words</span>
                  <span>{brief.read} read</span>
                  <span className="text-cyan-400">Verified across {brief.sources} sources</span>
                </div>
                <button
                  data-testid="brief-next-button"
                  onClick={() => setBriefIdx((i) => (i + 1) % briefs.length)}
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition-[filter,transform] duration-200 hover:brightness-110 active:scale-95"
                >
                  Next Brief
                  <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            )}

            {tab === "radar" && (
              <motion.div
                key="radar"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                data-testid="source-radar-panel"
              >
                <p className="text-xs font-mono-x uppercase tracking-widest text-slate-500">
                  One story: <span className="text-slate-300">Urban Transport Bill</span> — three newsrooms
                </p>
                <div className="relative mt-8 mb-3 h-2 rounded-full bg-gradient-to-r from-sky-500/60 via-slate-600 to-amber-500/60">
                  {radar.map((r) => (
                    <span
                      key={r.name}
                      style={{ left: r.pos }}
                      className="absolute -top-1 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-slate-950 bg-slate-100"
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] font-mono-x uppercase tracking-widest text-slate-500 mb-8">
                  <span>Left</span>
                  <span>Center</span>
                  <span>Right</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {radar.map((r) => (
                    <div key={r.name} data-testid={`radar-card-${r.lean.toLowerCase().replace(/[^a-z]/g, "-")}`} className="glow-card rounded-xl border border-[#1E293B] bg-slate-900/70 p-5">
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-mono-x uppercase tracking-widest ${r.color}`}>{r.lean}</span>
                        <span className="text-[10px] font-mono-x uppercase tracking-widest text-slate-500">{r.tone}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-3">{r.name}</p>
                      <p className="text-sm text-slate-200 leading-snug mt-1 font-medium">"{r.headline}"</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {tab === "fact" && (
              <motion.div
                key="fact"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}
                className="max-w-3xl"
                data-testid="fact-check-panel"
              >
                <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-mono-x uppercase tracking-widest text-red-300">Flagged claim — viral on social media</p>
                      <p className="text-slate-200 mt-1.5 font-medium">
                        "Metro fares doubled overnight in Delhi — video proof inside."
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-5">
                  {signals.map((s, i) => (
                    <div key={s.label} data-testid={`fact-signal-${i}`}>
                      <div className="flex justify-between text-xs font-mono-x uppercase tracking-widest text-slate-400">
                        <span>{s.label}</span>
                        <span>{s.value}/100</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-800 mt-2 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${s.value}%` }}
                          transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: "easeOut" }}
                          className={`h-full rounded-full ${s.value > 40 ? "bg-amber-400" : "bg-red-400"}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex items-center gap-3 rounded-xl border border-[#1E293B] bg-slate-900/70 p-4">
                  <BadgeCheck className="h-5 w-5 text-red-400 shrink-0" />
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-red-300">Verdict: Misleading.</span> Fare hike applies
                    to one corridor only; the video is from a 2019 protest. Trust score: 32/100.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Playground;
